import { useState } from "react";
import { IonSelect, IonSelectOption, useIonRouter } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { RequestModel } from "model";
import { useAuth } from "pages";
import { Md1K, MdArrowRight, MdCalendarMonth, MdInfo, MdLocationPin } from "react-icons/md";
import { Link } from "react-router-dom";
import { createRequestDetailsPath, createSubProfilePath } from "routes/routeData";
import { Card } from "swing-components";

import { Loader } from "~components/Atoms";
import { fetchRequestIndex, getFeatureFlags } from "~utils";

import { schoolReducer, subReducer } from "./utils";
import styles from "./Waitlist.module.css";

export type SubItem = {
  id: string;
  name: string;
  photo: string;
  signedUpRequests: number;
};

export type SchoolItem = {
  id: string;
  name: string;
  waitlistedRequests: number;
};

export function WaitlistPage() {
  const { data } = useQuery({
    queryKey: ["waitlistRequests"],
    queryFn: async () => {
      const res = await fetchRequestIndex({
        showHistory: false,
      });
      return {
        ...res,
        reqs: res.data.map((requestData: unknown) => new RequestModel(requestData)),
      };
    },
    refetchOnWindowFocus: false,
  });

  const subReqs: RequestModel[] = data?.reqs;

  const [filteredSub, setFilteredSub] = useState();
  const [filteredSchool, setFilteredSchool] = useState();

  const router = useIonRouter();
  const { userInfo } = useAuth();
  const { WAITLIST_TOOL } = getFeatureFlags(userInfo);

  if (!subReqs) return <Loader />;

  if (!WAITLIST_TOOL) router.push("/");

  // Unique array of all subs in all pools in the waitlist req index

  const subs = subReqs.reduce(subReducer, [] as SubItem[]);

  // Unique array of all schools in the waitlist req index

  const schools = subReqs.reduce(schoolReducer, [] as SchoolItem[]);

  const selectedSub = subs.find((s) => s.id === filteredSub);
  const selectedSchool = schools.find((s) => s.id === filteredSchool);

  const filteredSubList = subReqs
    // Filter by school first
    .filter((item) => {
      if (!filteredSchool) return true;
      return item.school.id === filteredSchool;
    })
    // Then filter by sub
    .filter((item) => {
      if (!filteredSub) return true;
      return item.pool.some((sub) => sub.id === filteredSub);
    });

  return (
    <div className="mw-800">
      <h1 className="mb-4">Waitlist Information</h1>

      {/* Sub Filter */}
      <IonSelect
        label="Select a sub to filter by"
        className="mb-4"
        onIonChange={(e) => setFilteredSub(e.target.value)}
      >
        <IonSelectOption value="">Clear Selection</IonSelectOption>

        {subs.map((item) => (
          <IonSelectOption key={item.id} value={item.id}>
            {item.name}
          </IonSelectOption>
        ))}
      </IonSelect>

      {/* School Filter */}
      <IonSelect
        label="Select a school to filter by"
        className="mb-4"
        onIonChange={(e) => setFilteredSchool(e.target.value)}
      >
        <IonSelectOption value="">Clear Selection</IonSelectOption>

        {schools.map((item) => (
          <IonSelectOption key={item.id} value={item.id}>
            {item.name}
          </IonSelectOption>
        ))}
      </IonSelect>

      {filteredSub && selectedSub && (
        <p>
          {selectedSub?.name} is in {selectedSub?.signedUpRequests} waitlist for all schools.
          {selectedSub?.signedUpRequests > 1 ? "s" : ""}.
        </p>
      )}

      {filteredSchool && selectedSchool && (
        <p>
          {selectedSchool?.name} has {selectedSchool?.waitlistedRequests} waitlisted request
          {selectedSchool?.waitlistedRequests > 1 ? "s" : ""}.
        </p>
      )}

      <div className={styles.waitlistList}>
        {filteredSubList.map((request, idx) => {
          return (
            <Card key={idx}>
              <div className="flex gap-2 items-center mb-2">
                <MdLocationPin className="icon-2xl" />
                <h3 className="m-0">{request.school.name}</h3>
              </div>
              <div className="flex gap-2 items-center mb-2">
                <MdCalendarMonth className="icon-2xl" />
                <h4 className="m-0">{request.clientDate}</h4>
              </div>
              <div className="flex gap-2 items-center mb-2">
                <MdInfo className="icon-2xl" />
                <h4 className="m-0">{request.id}</h4>
              </div>
              <Link
                className="flex gap-2 items-center mb-2"
                to={createRequestDetailsPath("active-requests", request.id)}
              >
                <Md1K className="icon-2xl" />
                <h4 className="m-0">View Request</h4>
              </Link>

              <h2>Subs In Pool</h2>
              {request.pool.map((sub, idx) => (
                <Link
                  className={`${styles.waitlistRow} swing-link`}
                  key={idx}
                  to={createSubProfilePath("active-requests", sub.id)}
                >
                  <img src={sub.photo} className="swing-profile-image" alt={sub.name} />
                  <div>{sub.name}</div>
                  <div>{sub.email}</div>
                  <MdArrowRight />
                </Link>
              ))}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
